import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faCheckCircle,
  faExclamationCircle,
} from '@fortawesome/free-solid-svg-icons';
import { submitContactForm } from '../services/contactService';

const initialFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  comments: '',
};

export default function ContactForm({ className = '', onSubmitted }) {
  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    if (!formData.firstName.trim() || !formData.email.trim()) {
      setError('First name and email address are required.');
      return;
    }

    try {
      setSubmitting(true);
      await submitContactForm(formData);
      setSuccess(true);
      setFormData(initialFormData);
      if (typeof onSubmitted === 'function') {
        onSubmitted();
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={className}>
      {success ? (
        <div
          style={{
            textAlign: 'center',
            padding: '60px 24px',
            background: '#f0fdf4',
            borderRadius: 16,
            border: '1.5px solid #bbf7d0',
          }}
        >
          <FontAwesomeIcon
            icon={faCheckCircle}
            style={{ fontSize: 52, color: '#16a34a', marginBottom: 20 }}
          />
          <h3
            style={{
              color: '#002536',
              fontWeight: 800,
              fontSize: 22,
              marginBottom: 10,
            }}
          >
            Message Sent!
          </h3>
          <p
            style={{
              color: '#5a7a8a',
              fontSize: 15,
              lineHeight: 1.7,
              marginBottom: 28,
            }}
          >
            Thank you for reaching out. We've received your message and will get
            back to you shortly.
          </p>
          <button
            className="btn-submit-contact"
            style={{ display: 'inline-flex', width: 'auto', padding: '13px 32px' }}
            onClick={() => setSuccess(false)}
          >
            Send Another Message
          </button>
        </div>
      ) : (
        <form className="contact-form" onSubmit={handleSubmit} noValidate>
          {error && (
            <div
              style={{
                background: '#fff1f0',
                border: '1.5px solid #ffc2be',
                borderRadius: 10,
                padding: '11px 16px',
                marginBottom: 20,
                fontSize: 13.5,
                color: '#c0392b',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <FontAwesomeIcon icon={faExclamationCircle} /> {error}
            </div>
          )}

          <div className="row g-4">
            <div className="col-md-6">
              <div className="form-group-contact">
                <label htmlFor="firstName" className="form-label-contact">
                  First Name <span className="required-star">*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  className="form-input-contact"
                  placeholder="John"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-group-contact">
                <label htmlFor="lastName" className="form-label-contact">
                  Last Name <span className="required-star">*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  className="form-input-contact"
                  placeholder="Doe"
                  value={formData.lastName}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-group-contact">
                <label htmlFor="email" className="form-label-contact">
                  Email Address <span className="required-star">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="form-input-contact"
                  placeholder="john.doe@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-group-contact">
                <label htmlFor="phone" className="form-label-contact">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  className="form-input-contact"
                  placeholder="+1 (555) 000-0000"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="col-12">
              <div className="form-group-contact">
                <label htmlFor="comments" className="form-label-contact">
                  Additional Comments
                </label>
                <textarea
                  id="comments"
                  name="comments"
                  className="form-textarea-contact"
                  placeholder="Tell us about your project..."
                  rows="3"
                  value={formData.comments}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="col-12">
              <button
                type="submit"
                className="btn-submit-contact"
                disabled={submitting}
                style={{
                  opacity: submitting ? 0.75 : 1,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                }}
              >
                {submitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    &nbsp; Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
